import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const GUEST_KEY = 'protech_cart_guest';

const readCart = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Cart entries only need enough of the product to render the cart and to
// build the order payload. Storing the whole document (reviews included)
// bloats localStorage and can blow past the quota.
const toCartItem = (product, quantity) => ({
  _id: product._id,
  name: product.name,
  brand: product.brand,
  category: product.category,
  price: product.price,
  discount: product.discount || 0,
  stock: typeof product.stock === 'number' ? product.stock : 0,
  images: product.images?.slice(0, 1) || [],
  quantity,
});

const clampQuantity = (quantity, stock) => {
  const max = typeof stock === 'number' && stock > 0 ? stock : 1;
  return Math.max(1, Math.min(Math.trunc(quantity) || 1, max));
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const cartStorageKey = user ? `protech_cart_${user._id}` : GUEST_KEY;

  const [cartItems, setCartItems] = useState(() => readCart(cartStorageKey));
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Tracks which key the current cartItems belong to. Without this the
  // "save" effect fires before the "load" effect's state update lands and
  // writes the previous user's cart into the new key.
  const activeKeyRef = useRef(cartStorageKey);

  useEffect(() => {
    if (activeKeyRef.current === cartStorageKey) return;
    activeKeyRef.current = cartStorageKey;
    setCartItems(readCart(cartStorageKey));
  }, [cartStorageKey]);

  useEffect(() => {
    if (activeKeyRef.current !== cartStorageKey) return;
    try {
      localStorage.setItem(cartStorageKey, JSON.stringify(cartItems));
    } catch (err) {
      // Quota exceeded or storage disabled — the cart still works for this
      // session, it just will not survive a reload.
      console.error('Could not persist cart:', err);
    }
  }, [cartItems, cartStorageKey]);

  const addToCart = useCallback((product, quantity = 1) => {
    if (!product?._id) return;
    setCartItems((prev) => {
      const existing = prev.find((i) => i._id === product._id);
      if (existing) {
        const newQty = clampQuantity(existing.quantity + quantity, product.stock ?? existing.stock);
        return prev.map((i) => (i._id === product._id ? { ...i, quantity: newQty } : i));
      }
      return [...prev, toCartItem(product, clampQuantity(quantity, product.stock))];
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCartItems((prev) => prev.filter((i) => i._id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    setCartItems((prev) => {
      if (quantity <= 0) return prev.filter((i) => i._id !== productId);
      return prev.map((i) =>
        i._id === productId ? { ...i, quantity: clampQuantity(quantity, i.stock) } : i
      );
    });
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const cartCount = cartItems.reduce((acc, i) => acc + i.quantity, 0);
  const subtotal = cartItems.reduce((acc, i) => {
    const price = i.discount > 0 ? Math.round(i.price * (1 - i.discount / 100)) : i.price;
    return acc + price * i.quantity;
  }, 0);
  const tax = Math.round(subtotal * 0.18);
  const shipping = subtotal > 1000 || subtotal === 0 ? 0 : 99;
  const total = subtotal + tax + shipping;

  return (
    <CartContext.Provider value={{
      cartItems, cartCount, subtotal, tax, shipping, total,
      isCartOpen, setIsCartOpen,
      addToCart, removeFromCart, updateQuantity, clearCart,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
